import { css, html, LitElement } from 'lit';
import { RequesterMixin } from '@brightspace-ui/core/mixins/provider-mixin';

/**
 * Task Manager Component
 *
 * A component that displays all tasks for the current user with the ability to refresh.
 * Supports both determinate and indeterminate tasks.
 *
 * @example
 * <nova-task-manager></nova-task-manager>
 */
export class TaskManager extends RequesterMixin(LitElement) {
  static get properties() {
    return {
      environmentId: { type: String, reflect: true, attribute: 'environment-id' },
      tasks: { type: Array, state: true },
      loading: { type: Boolean, state: true },
      error: { type: String, state: true },
      statusFilter: { type: String, state: true },
      timeRangeFilter: { type: String, state: true },
      sortBy: { type: String, state: true },
      sortDirection: { type: String, state: true },
      expandedTasks: { type: Object, state: true },
      nextToken: { type: String, state: true },
      loadingMore: { type: Boolean, state: true },
    };
  }

  static get styles() {
    return css`
      :host {
        display: block;
        font-family: var(--nova-font-family, sans-serif);
        color: #333;
      }

      .task-manager {
        border: 1px solid #e0e0e0;
        border-radius: 4px;
        overflow: hidden;
        background: #fff;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      }

      .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 1rem;
        background: #f5f5f5;
        border-bottom: 1px solid #e0e0e0;
      }

      h2 {
        margin: 0;
        font-size: 1.2rem;
      }

      .controls {
        display: flex;
        gap: 0.5rem;
        align-items: center;
      }

      .refresh-button {
        background: #2196f3;
        color: white;
        border: none;
        border-radius: 4px;
        padding: 0.5rem;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1rem;
        transition: background 0.2s;
      }

      .refresh-button:hover {
        background: #1976d2;
      }

      .refresh-button svg {
        width: 16px;
        height: 16px;
        margin-right: 0.25rem;
      }

      .refresh-button.loading {
        opacity: 0.7;
        cursor: not-allowed;
      }

      .refresh-button.loading svg {
        animation: spin 1s linear infinite;
      }

      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }

      .filter-control {
        display: flex;
        align-items: center;
        gap: 0.5rem;
      }

      select {
        padding: 0.4rem;
        border: 1px solid #ddd;
        border-radius: 4px;
        background: white;
        font-size: 0.9rem;
      }

      .task-list {
        max-height: 600px;
        overflow-y: auto;
      }

      .task-item {
        padding: 1rem;
        border-bottom: 1px solid #eee;
        transition: background 0.2s;
      }

      .task-item:hover {
        background: #f9f9f9;
      }

      .task-item-header {
        display: flex;
        justify-content: space-between;
        margin-bottom: 0.5rem;
      }

      .task-title {
        font-weight: bold;
        margin: 0;
      }

      .task-type {
        color: #666;
        font-size: 0.85rem;
      }

      .task-status {
        padding: 0.2rem 0.5rem;
        border-radius: 3px;
        font-size: 0.8rem;
        text-transform: uppercase;
      }

      .status-pending {
        background: #f0f0f0;
        color: #666;
        height: 25px;
      }

      .status-in_progress {
        background: #e3f2fd;
        color: #0d47a1;
        height: 25px;
      }

      .status-completed {
        background: #e8f5e9;
        color: #1b5e20;
        height: 25px;
      }

      .status-failed {
        background: #ffebee;
        color: #b71c1c;
        height: 25px;
      }

      .progress-container {
        height: 8px;
        background: #eee;
        border-radius: 4px;
        overflow: hidden;
        margin: 0.5rem 0;
      }

      .progress-bar {
        height: 100%;
        background: #2196f3;
        transition: width 0.3s ease;
      }

      .progress-bar.completed {
        background: #4caf50;
      }

      .progress-bar.failed {
        background: #f44336;
      }

      .progress-bar.indeterminate {
        width: 50%;
        background: linear-gradient(to right, transparent, #2196f3, transparent);
        animation: indeterminate-progress 1.5s infinite ease-in-out;
      }

      @keyframes indeterminate-progress {
        0% { transform: translateX(-100%); }
        100% { transform: translateX(200%); }
      }

      .task-details {
        display: flex;
        justify-content: space-between;
        font-size: 0.85rem;
        color: #666;
      }

      .task-message {
        margin-top: 0.5rem;
        font-size: 0.9rem;
      }

      .task-data {
        margin-top: 1rem;
        padding: 0.5rem;
        background: #f5f5f5;
        border-radius: 4px;
        font-size: 0.9rem;
      }

      .completion-data {
        margin-top: 0.5rem;
        padding: 0.5rem;
        background: #e8f5e9;
        border-radius: 4px;
        font-size: 0.9rem;
        border-left: 3px solid #4caf50;
      }

      .data-container {
        display: flex;
        gap: 1rem;
        margin-top: 1rem;
        overflow: hidden;
        max-height: 0;
        transition: max-height 0.3s ease-out;
      }

      .data-container.expanded {
        max-height: 500px;
        transition: max-height 0.5s ease-in;
      }

      .data-container > div {
        flex: 1;
        margin-top: 0;
      }

      .toggle-data-button {
        background: none;
        border: none;
        color: #2196f3;
        cursor: pointer;
        font-size: 0.9rem;
        padding: 0.5rem 0;
        display: flex;
        align-items: center;
        margin-top: 0.5rem;
      }

      .toggle-data-button:hover {
        text-decoration: underline;
      }

      .toggle-data-button svg {
        width: 16px;
        height: 16px;
        margin-right: 0.25rem;
        transition: transform 0.3s;
      }

      .toggle-data-button.expanded svg {
        transform: rotate(180deg);
      }

      .task-actions {
        margin-top: 0.5rem;
        display: flex;
        gap: 0.5rem;
      }

      .action-button {
        background: #2196f3;
        color: white;
        border: none;
        border-radius: 4px;
        padding: 0.3rem 0.6rem;
        font-size: 0.8rem;
        cursor: pointer;
      }

      .action-button:hover {
        background: #1976d2;
      }

      .empty-state {
        padding: 2rem;
        text-align: center;
        color: #666;
      }

      .error-message {
        padding: 1rem;
        background: #ffebee;
        color: #b71c1c;
        margin: 0;
      }

      .loading-overlay {
        padding: 2rem;
        text-align: center;
        color: #666;
      }

      .loading-spinner {
        display: inline-block;
        width: 2rem;
        height: 2rem;
        border: 3px solid rgba(0, 0, 0, 0.1);
        border-radius: 50%;
        border-top-color: #2196f3;
        animation: spin 1s linear infinite;
        margin-bottom: 1rem;
      }

      .sort-button {
        background: none;
        border: none;
        cursor: pointer;
        display: flex;
        align-items: center;
        color: #666;
        font-size: 0.8rem;
        padding: 0.2rem 0.4rem;
        border-radius: 3px;
      }

      .sort-button:hover {
        background: #f0f0f0;
      }

      .sort-icon {
        margin-left: 0.25rem;
      }

      .pagination {
        display: flex;
        justify-content: center;
        padding: 1rem;
        gap: 0.5rem;
      }

      .pagination-button {
        padding: 0.25rem 0.5rem;
        border: 1px solid #ddd;
        background: white;
        border-radius: 3px;
        cursor: pointer;
      }

      .pagination-button:hover {
        background: #f0f0f0;
      }

      .pagination-button.active {
        background: #2196f3;
        color: white;
        border-color: #2196f3;
      }

      .pagination-button.disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .load-more-container {
        display: flex;
        justify-content: center;
        padding: 1rem;
      }

      .load-more-button {
        background: #2196f3;
        color: white;
        border: none;
        border-radius: 4px;
        padding: 0.6rem 1.2rem;
        font-size: 0.9rem;
        cursor: pointer;
        transition: background 0.2s;
      }

      .load-more-button:hover {
        background: #1976d2;
      }

      .load-more-button:disabled,
      .load-more-button.loading {
        opacity: 0.7;
        cursor: not-allowed;
      }
    `;
  }

  constructor() {
    super();
    this.tasks = [];
    this.loading = false;
    this.error = null;
    this.statusFilter = 'all';
    this.timeRangeFilter = 'all';
    this.sortBy = 'startTime';
    this.sortDirection = 'desc';
    this.expandedTasks = {};
    this.nextToken = null;
    this.loadingMore = false;
  }

  connectedCallback() {
    super.connectedCallback();
    this.client = this.requestInstance('d2l-pm-client');
    this.fetchTasks();
  }

  /**
   * Fetch all tasks for the current user/tenant
   * @param {boolean} showLoading - Whether to show loading state, defaults to true for manual refreshes
   * @param {boolean} loadMore - Whether this is a load more request
   */
  async fetchTasks(showLoading = true, loadMore = false) {
    if (showLoading && !loadMore) {
      this.loading = true;
    }

    if (loadMore) {
      this.loadingMore = true;
    }

    this.error = null;

    try {
      // Build filter object for the updated client method
      const filters = {
        limit: 20,
      };

      // Only add nextToken for load more requests
      if (loadMore && this.nextToken) {
        filters.nextToken = this.nextToken;
      } else if (loadMore && !this.nextToken) {
        // If trying to load more but no nextToken exists
        this.loadingMore = false;
        return;
      }

      // Only add status filter if not set to 'all'
      if (this.statusFilter && this.statusFilter !== 'all') {
        filters.status = this.statusFilter;
      }

      // Add time range filter if not set to 'all'
      if (this.timeRangeFilter && this.timeRangeFilter !== 'all') {
        // Send the timeRange directly to the API
        filters.timeRange = this.timeRangeFilter;
      }

      // Call the updated client method with filters
      const data = await this.client.fetchTasks(this.environmentId, filters);

      // Store the nextToken for pagination
      this.nextToken = data.nextToken || null;

      // For load more, append the new tasks to existing ones
      if (loadMore) {
        this.tasks = [...this.tasks, ...(data.tasks || [])];
      } else {
        // For initial load or refresh, replace the tasks
        this.tasks = data.tasks || [];
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
      this.error = `Error: ${error.message}`;
    } finally {
      if (showLoading && !loadMore) {
        this.loading = false;
      }

      if (loadMore) {
        this.loadingMore = false;
      }
    }
  }

  /**
   * Manual refresh method
   */
  refresh() {
    this.nextToken = null; // Reset pagination
    this.fetchTasks(true, false);
  }

  /**
   * Handle status filter change
   */
  handleStatusFilterChange(e) {
    this.statusFilter = e.target.value;
    this.nextToken = null; // Reset pagination when filter changes
    this.fetchTasks();
  }

  /**
   * Handle time range filter change
   */
  handleTimeRangeFilterChange(e) {
    this.timeRangeFilter = e.target.value;
    this.nextToken = null; // Reset pagination when filter changes
    this.fetchTasks();
  }

  /**
   * Handle sort by change
   * @param {string} field - Field to sort by
   */
  handleSortChange(field) {
    if (this.sortBy === field) {
      // Toggle direction if clicking the same field
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortBy = field;
      this.sortDirection = 'desc'; // Default to descending for new field
    }
  }

  /**
   * Set data for a completed task
   * @param {string} taskId - ID of the task to update
   * @param {Object} data - Data to set on the task
   */
  async setTaskData(taskId, data) {
    if (!taskId || !data) {
      console.error('Task ID and data are required');
      return false;
    }

    try {
      await this.client.updateTaskExecution(taskId, { data });
      // Refresh tasks to get updated data
      this.fetchTasks();
      return true;
    } catch (error) {
      console.error('Error updating task data:', error);
      this.error = `Error updating task: ${error.message}`;
      return false;
    }
  }

  /**
   * Filter tasks based on status
   */
  getFilteredTasks() {
    if (!this.tasks.length) return [];

    const filtered = [...this.tasks];

    // Apply sorting only - filters are now applied server-side
    filtered.sort((a, b) => {
      let valA = a[this.sortBy];
      let valB = b[this.sortBy];

      // Special case for dates
      if (this.sortBy === 'startTime' || this.sortBy === 'endTime' || this.sortBy === 'lastUpdated') {
        valA = valA ? new Date(valA).getTime() : 0;
        valB = valB ? new Date(valB).getTime() : 0;
      }

      // Compare based on direction
      const compareResult = valA > valB ? 1 : valA < valB ? -1 : 0;
      return this.sortDirection === 'asc' ? compareResult : -compareResult;
    });

    return filtered;
  }

  /**
   * Format a date for display
   */
  formatDate(dateString) {
    if (!dateString) return '';

    const date = new Date(dateString);
    return date.toLocaleString();
  }

  /**
   * Calculate duration between start and end time
   */
  formatDuration(startTime, endTime, status, lastUpdated) {
    if (!startTime) return '';

    const start = new Date(startTime);
    // For completed or failed tasks, use the provided endTime or lastUpdated time
    // For in-progress tasks, use the current time
    let end;
    if (status === 'completed' || status === 'failed') {
      end = endTime ? new Date(endTime) : (lastUpdated ? new Date(lastUpdated) : new Date());
    } else {
      end = new Date();
    }

    const durationMs = end - start;
    const seconds = Math.floor(durationMs / 1000);

    if (seconds < 60) {
      return `${seconds} seconds`;
    }

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    if (minutes < 60) {
      return `${minutes}m ${remainingSeconds}s`;
    }

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    return `${hours}h ${remainingMinutes}m ${remainingSeconds}s`;
  }

  /**
   * Get status message based on status and completion percentage
   * @private
   */
  _getStatusMessage(status, percentComplete) {
    if (status === 'pending') return 'Task scheduled';
    if (status === 'completed') return 'Task completed successfully';
    if (status === 'failed') return 'Task failed: An error occurred during processing';

    // For in-progress with percentComplete available
    if (percentComplete !== undefined && percentComplete !== null) {
      if (percentComplete < 20) return 'Initializing task resources...';
      if (percentComplete < 50) return 'Processing data...';
      if (percentComplete < 80) return 'Processing additional records...';
      return 'Finalizing results...';
    }

    // For indeterminate in-progress tasks
    return 'Processing...';
  }

  /**
   * Render progress bar for a task
   * @private
   */
  _renderProgressBar(task) {
    // For completed or failed tasks
    if (task.status === 'completed') {
      return html`<div class="progress-bar completed" style="width: 100%"></div>`;
    }

    if (task.status === 'failed') {
      return html`<div class="progress-bar failed" style="width: 100%"></div>`;
    }

    // For indeterminate tasks (when in_progress or pending)
    if (task.isIndeterminate && (task.status === 'in_progress' || task.status === 'pending')) {
      return html`<div class="progress-bar indeterminate"></div>`;
    }

    // For pending tasks
    if (task.status === 'pending') {
      return html`<div class="progress-bar" style="width: 0%"></div>`;
    }

    // For in-progress tasks
    if (task.percentComplete !== undefined && task.percentComplete !== null) {
      // Determinate progress
      return html`<div class="progress-bar" style="width: ${task.percentComplete}%"></div>`;
    } else {
      // Indeterminate progress as fallback
      return html`<div class="progress-bar indeterminate"></div>`;
    }
  }

  /**
   * Format task data for display
   * @private
   */
  _formatTaskData(data) {
    if (!data) return '';

    try {
      if (typeof data === 'string') {
        return data;
      }
      return JSON.stringify(data, null, 2);
    } catch (error) {
      return `Error formatting data: ${error.message}`;
    }
  }

  /**
   * Load more tasks
   */
  loadMoreTasks() {
    if (this.nextToken && !this.loadingMore) {
      this.fetchTasks(false, true);
    }
  }

  render() {
    const filteredTasks = this.getFilteredTasks();

    return html`
      <div class="task-manager">
        <div class="header">
          <h2>Tasks</h2>
          <div class="controls">
            <div class="filter-control">
              <label for="status-filter">Status:</label>
              <select id="status-filter" @change="${this.handleStatusFilterChange}" .value="${this.statusFilter}">
                <option value="all">All</option>
                <option value="pending">Pending</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
              </select>
            </div>
            <button class="refresh-button ${this.loading ? 'loading' : ''}"
                    @click="${this.refresh}"
                    ?disabled="${this.loading}">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
              </svg>
            </button>
          </div>
        </div>

        ${this.error ? html`<p class="error-message">${this.error}</p>` : ''}

        ${this.loading && !this.tasks.length ? html`
          <div class="loading-overlay">
            <div class="loading-spinner"></div>
            <p>Loading tasks...</p>
          </div>
        ` : ''}

        ${!this.loading && !filteredTasks.length ? html`
          <div class="empty-state">
            <p>No tasks found${this.statusFilter !== 'all' ? ` with status "${this.statusFilter}"` : ''}.</p>
          </div>
        ` : ''}

        ${filteredTasks.length ? html`
          <div class="task-list">
            ${filteredTasks.map(task => html`
              <div class="task-item">
                <div class="task-item-header">
                  <div>
                    <h3 class="task-title">${this._getTaskTitle(task.taskType)}</h3>
                    <div class="task-type">${task.taskType}</div>
                  </div>
                  <span class="task-status status-${task.status}">${task.status}</span>
                </div>

                <div class="progress-container">
                  ${this._renderProgressBar(task)}
                </div>

                ${task.statusMessage ? html`
                  <div class="task-message">${task.statusMessage}</div>
                ` : html`
                  <div class="task-message">${this._getStatusMessage(task.status, task.percentComplete)}</div>
                `}

                <div class="task-details">
                  <span>ID: ${task.id.substring(0, 8)}...</span>
                  <span>Started: ${this.formatDate(task.startTime)}</span>
                  <span>${task.endTime ? `Completed: ${this.formatDate(task.endTime)}` : `Duration: ${this.formatDuration(task.startTime, task.endTime, task.status, task.lastUpdated)}`}</span>
                </div>

                ${(task.data || task.completionData) ? html`
                  <button
                    class="toggle-data-button ${this.expandedTasks[task.id] ? 'expanded' : ''}"
                    @click="${() => this.toggleTaskData(task.id)}">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
                    </svg>
                    ${this.expandedTasks[task.id] ? 'Hide' : 'Show'} Data
                  </button>

                  <div class="data-container ${this.expandedTasks[task.id] ? 'expanded' : ''}">
                    ${task.data ? html`
                      <div class="task-data">
                        <strong>Task Data:</strong>
                        <pre>${this._formatTaskData(task.data)}</pre>
                      </div>
                    ` : ''}

                    ${task.completionData ? html`
                      <div class="completion-data">
                        <strong>Completion Data:</strong>
                        <pre>${this._formatTaskData(task.completionData)}</pre>
                      </div>
                    ` : ''}
                  </div>
                ` : ''}

                ${task.status === 'completed' && task.taskType === 'task-execution' ? html`
                  <div class="task-actions">
                    <button class="action-button" @click="${() => this._showDataDialog(task.id)}">
                      Set Data
                    </button>
                  </div>
                ` : ''}
              </div>
            `)}

            ${this.nextToken ? html`
              <div class="load-more-container">
                <button
                  class="load-more-button ${this.loadingMore ? 'loading' : ''}"
                  @click="${this.loadMoreTasks}"
                  ?disabled="${this.loadingMore}">
                  ${this.loadingMore ? 'Loading...' : 'Load More'}
                </button>
              </div>
            ` : ''}
          </div>
        ` : ''}
      </div>
    `;
  }

  /**
   * Show dialog to set data for a task
   * @private
   */
  _showDataDialog(taskId) {
    // Create a simple prompt for data
    const data = prompt('Enter data for the task (as JSON or string):');
    if (data === null) return; // User cancelled

    try {
      // Try to parse as JSON, but fall back to string if it fails
      const parsedData = data.trim().startsWith('{') ? JSON.parse(data) : data;
      this.setTaskData(taskId, parsedData);
    } catch (error) {
      // If JSON parsing fails, use as string
      this.setTaskData(taskId, data);
    }
  }

  /**
   * Get a user-friendly title for a task type
   */
  _getTaskTitle(taskType) {
    // Convert kebab-case or snake_case to Title Case
    return taskType
      .replace(/[-_]/g, ' ')
      .replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase());
  }

  /**
   * Toggle expanded state for a task
   * @param {string} taskId - ID of the task to toggle
   */
  toggleTaskData(taskId) {
    this.expandedTasks = {
      ...this.expandedTasks,
      [taskId]: !this.expandedTasks[taskId],
    };
  }
}

customElements.define('nova-task-manager', TaskManager);
